import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Modal } from "./ui";
import Icon from "./Icon";

// Per-screen help manual.
const HELP = {
  "/": {
    title: "Mis Tareas",
    items: [
      "Tu tablero personal con las tareas asignadas a ti.",
      "Cambia entre vistas Kanban, Lista y Timeline con el selector.",
      "Filtra por prioridad, estado o usa el buscador.",
      "Crea tareas con “Nueva”, o dicta el título/descripción con el botón de micrófono.",
      "Exporta a CSV/Excel, o importa desde una plantilla (botón con el ícono de descarga).",
      "Arrastra tarjetas entre columnas (Kanban) para cambiar su estado.",
    ],
  },
  "/departamento": {
    title: "Tablero de Departamento",
    items: [
      "Tareas de tu departamento (y los anidados debajo en el organigrama).",
      "Filtros combinables: responsable, prioridad, estado.",
      "Guarda tus filtros favoritos con el botón de guardar (Vistas).",
      "Selecciona una Quincena para ver ese ciclo; con una quincena activa puedes “Cerrar quincena” (informe).",
      "Managers/admins pueden importar tareas desde Excel.",
    ],
  },
  "/proyectos": {
    title: "Proyectos",
    items: [
      "Tablero exclusivo de proyectos. A la izquierda eliges un proyecto; a la derecha ves SOLO sus tareas.",
      "Cada proyecto tiene tipo (Desarrollo, Apertura, Gestión, Entrenamiento o uno nuevo), responsable y fecha de entrega.",
      "Filtra por tipo, responsable o nombre.",
      "Cuatro vistas de las tareas del proyecto: Kanban, Lista, Timeline y Mapa de dependencias.",
      "“Editar” cambia los parámetros del proyecto; “Duplicar” copia el proyecto con todas sus tareas.",
      "“Tarea” crea una tarea ya asociada al proyecto seleccionado.",
    ],
  },
  "/directorio": {
    title: "Directorio de correos",
    items: [
      "Contactos externos para enviar correos — separado de los usuarios del sistema (no se mezclan).",
      "Campos: empresa, país, teléfono, tienda, marca, tipo (Proveedor, Country/Brand Manager, Colaborador, Gerente de Tienda) y etiquetas.",
      "Al enviar un informe o tareas por correo, el selector busca por todos estos campos y permite Para / CC / CCO.",
      "El remitente del correo es el email del usuario que envía (no el de la configuración SMTP).",
    ],
  },
  "/timeline": {
    title: "Timeline / Gantt",
    items: [
      "Cronograma horizontal de tareas por responsable.",
      "Zoom: Semana / Mes / Trimestre.",
      "Las flechas muestran dependencias entre tareas.",
      "Las barras con borde rojo indican solapamiento de carga de una misma persona.",
      "La línea “hoy” marca la fecha actual. Clic en una barra abre el detalle.",
    ],
  },
  "/dashboard": {
    title: "Reportes / Dashboard",
    items: [
      "KPIs en tiempo real: total, completadas, en progreso, bloqueadas, vencidas y % cumplimiento.",
      "Desglose por coordinador con semáforo de estado.",
      "Capacidad del equipo: detecta sobrecargados; activa “Reasignar” para arrastrar tareas entre personas.",
      "Alertas accionables: bloqueadas, vencidas, de la semana y alta prioridad.",
      "Filtra por departamento, prioridad y rango de fechas (botón Última semana).",
      "“Informe Gerencial” genera un resumen y lo envía por correo a quien elijas (requiere SMTP).",
    ],
  },
  "/metricas": {
    title: "Métricas",
    items: [
      "Velocity: tareas completadas por coordinador en cada quincena (histórico).",
      "Burndown: tareas restantes vs. línea ideal dentro del ciclo seleccionado.",
    ],
  },
  "/documentacion": {
    title: "Documentación Departamental",
    items: [
      "Repositorio seguro de documentos organizado igual que el organigrama: cada carpeta es un departamento.",
      "Un documento solo lo ven su mismo departamento y los departamentos superiores (sus jefes); los demás no lo ven.",
      "Filtra por categoría (Contratos, Descripción de cargos, Procesos, Inventarios…) o busca por título.",
      "Confidencialidad por documento: “Restringido” (con ícono de candado) solo lo ven líderes o con quien se comparta; “Normal” lo ve todo el departamento (y superiores).",
      "Subir y ocultar/borrar: permiso (capacidad) + ser del mismo departamento o superior al directorio del archivo.",
      "Editar el contenido de un documento: solo el propietario (quien lo subió) o un superior.",
      "Si te comparten un documento puedes verlo y descargarlo, pero NO editarlo, borrarlo ni volver a compartirlo.",
      "Vista previa (icono de ojo) en la app para PDF e imágenes; los Office (Word/Excel/PPT) se descargan.",
      "Toda descarga, vista previa y compartición queda registrada: abre la “Bitácora” (icono de reloj) de un documento para ver quién accedió.",
    ],
  },
  "/bitacora": {
    title: "Bitácora",
    items: [
      "Registro completo de cambios: quién hizo qué y cuándo.",
      "Incluye creación, cambios de estado, comentarios, adjuntos e importaciones.",
    ],
  },
  "/perfil": {
    title: "Mi Perfil",
    items: [
      "Cambia tu avatar (foto) y tu contraseña.",
      "Tu departamento es de solo lectura (lo asigna el administrador).",
      "Elige qué notificaciones recibir y si quieres avisos del navegador.",
    ],
  },
  "/configuracion": {
    title: "Configuración",
    items: [
      "Permisos por perfil: define el alcance de cada rol (qué ve y qué puede hacer).",
      "SMTP: servidor de correo para enviar informes (usa “Probar SMTP”).",
      "Resumen ejecutivo: destinatarios y envío automático semanal.",
      "API para Claude/integraciones: clave para consultar los datos.",
      "Zoho Desk: importar tickets como tareas. Bots de Telegram/WhatsApp (config).",
      "Campos obligatorios por departamento al crear tareas.",
    ],
  },
  "/admin/usuarios": {
    title: "Usuarios",
    items: [
      "Crea y edita usuarios, asigna rol, cargo y departamento.",
      "Desactiva usuarios (no se borran, se conserva su historial).",
    ],
  },
  "/admin/departamentos": {
    title: "Departamentos",
    items: [
      "Vista de Organigrama y Lista.",
      "Crea departamentos y define su nivel superior (“Depende de”) para armar la jerarquía.",
      "Los departamentos superiores ven las tareas de los inferiores.",
    ],
  },
  "/mensajeria": { title: "Mensajería", items: [
    "La bandeja reúne conversaciones directas, grupos y canales asociados a tareas y proyectos.",
    "En móvil, abre una conversación y usa la flecha superior para volver a la lista.",
    "Usa el buscador para filtrar conversaciones y localizar mensajes por su contenido.",
    "Adjunta imágenes y documentos; se muestran el nombre y tamaño antes de enviar. El límite depende del canal (15 MB en tareas y 30 MB en chats directos/grupos).",
    "Pulsa un adjunto para abrir la vista previa o descargarlo. Solo los participantes autorizados pueden acceder al archivo.",
    "Puedes compartir enlaces desde el compositor: # para proyectos, * para tareas y ! para requisiciones.",
    "Los mensajes nuevos se actualizan mientras el hilo está abierto. Las acciones de editar/eliminar aparecen junto a tus propios mensajes.",
  ] },
  "/soporte": { title: "Soporte", items: [
    "La mesa de soporte reúne indicadores, tickets activos, vencimientos y accesos a la atención.",
    "Usa Bandeja de tickets para filtrar, asignar y dar seguimiento según tus permisos.",
    "En móvil, la barra inferior ofrece acceso rápido a Mesa, Tickets, Nuevo y WhatsApp.",
    "Los permisos del perfil pueden limitar la visibilidad y las acciones disponibles.",
  ] },
  "/compras": { title: "Compras", items: [
    "Compras organiza requisiciones, aprobaciones, órdenes, facturas y presupuesto según los módulos habilitados.",
    "Los botones disponibles dependen del módulo activo y de tus permisos de perfil.",
    "Antes de aprobar, revisa el detalle, los documentos adjuntos y el historial de la solicitud.",
  ] },
  "/mis-recursos": { title: "Mis recursos", items: [
    "Consulta equipos bajo tu custodia vigente y licencias asignadas directamente a tu usuario.",
    "Desde una tarjeta de equipo puedes reportar un problema o pedir mantenimiento.",
    "El equipo ITAM recibe una notificación y puede asignar responsable, actualizar el estado y registrar la respuesta.",
    "En Mis solicitudes puedes consultar el avance y la respuesta del equipo técnico.",
    "Solo se muestran los datos operativos del recurso necesarios para identificarlo; la información de compras y costos permanece restringida.",
  ] },
  "/itam/solicitudes": { title: "Solicitudes ITAM", items: [
    "La bandeja reúne problemas y solicitudes de mantenimiento que reportan los colaboradores sobre recursos asignados.",
    "Filtra por estado, asígnate una solicitud y actualiza el avance del trabajo.",
    "Usa Respuesta o notas para informar diagnóstico, próximos pasos o resolución al solicitante.",
    "Al cambiar estado, responsable o respuesta, la persona solicitante recibe una notificación.",
    "La bandeja respeta tu alcance de gestión por ubicación configurado en ITAM.",
  ] },
  "/activos": { title: "Activos", items: [
    "Consulta el inventario de equipos y abre cada activo para revisar sus datos e historial.",
    "Usa filtros y búsqueda para localizar activos; las acciones de edición dependen de tu perfil.",
  ] },
  "/actividad": { title: "Actividad y notificaciones", items: [
    "Revisa menciones, asignaciones, comentarios y otros eventos recientes.",
    "Filtra por tipo, origen, proyecto, estado de lectura o rango de fechas.",
    "Al abrir una notificación se marca como leída y se navega al registro relacionado cuando hay enlace.",
  ] },
  "/ayuda-general": { title: "Ayuda general", items: [
    "La navegación y las acciones visibles dependen de tu perfil, permisos y módulos habilitados.",
    "El buscador superior permite localizar tareas, proyectos y personas; la búsqueda contextual cambia según el módulo.",
    "Tu perfil permite configurar avatar y preferencias personales. La configuración global está reservada a perfiles autorizados.",
    "Si una acción no aparece o muestra acceso denegado, consulta al administrador para revisar permisos y alcance departamental.",
    "Para reportar un problema, comparte el módulo, el registro afectado y el mensaje de error; evita incluir contraseñas o tokens.",
  ] },
};

function matchHelp(path) {
  if (path.startsWith("/proyectos")) return HELP["/proyectos"];
  if (path.startsWith("/directorio")) return HELP["/directorio"];
  if (path.startsWith("/documentacion")) return HELP["/documentacion"];
  if (path.startsWith("/admin/usuarios")) return HELP["/admin/usuarios"];
  if (path.startsWith("/admin/departamentos")) return HELP["/admin/departamentos"];
  if (path.startsWith("/mensajeria")) return HELP["/mensajeria"];
  if (path.startsWith("/soporte")) return HELP["/soporte"];
  if (path.startsWith("/compras")) return HELP["/compras"];
  if (path.startsWith("/mis-recursos")) return HELP["/mis-recursos"];
  if (path.startsWith("/itam/solicitudes")) return HELP["/itam/solicitudes"];
  if (path.startsWith("/activos")) return HELP["/activos"];
  if (path.startsWith("/actividad")) return HELP["/actividad"];
  if (path.startsWith("/perfil")) return HELP["/perfil"];
  if (path.startsWith("/configuracion")) return HELP["/configuracion"];
  if (path.startsWith("/bitacora")) return HELP["/bitacora"];
  if (path.startsWith("/timeline")) return HELP["/timeline"];
  if (path.startsWith("/dashboard")) return HELP["/dashboard"];
  if (path.startsWith("/metricas")) return HELP["/metricas"];
  if (path.startsWith("/departamento")) return HELP["/departamento"];
  return HELP[path] || HELP["/ayuda-general"];
}

export default function HelpButton() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const h = matchHelp(loc.pathname);
  const [query, setQuery] = useState("");
  const [allHelp, setAllHelp] = useState(false);
  const entries = useMemo(() => {
    const source = allHelp ? Object.values(HELP) : [h];
    const q = query.trim().toLocaleLowerCase();
    return source.flatMap((section) => {
      const items = section.items.filter((item) => !q || `${section.title} ${item}`.toLocaleLowerCase().includes(q));
      return items.length ? [{ ...section, items }] : [];
    });
  }, [allHelp, h, query]);
  return (
    <>
      <button onClick={() => { setOpen(true); setQuery(""); setAllHelp(false); }} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Ayuda de esta pantalla" aria-label="Ayuda">
        <Icon name="help" size={19} />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title={`Ayuda — ${allHelp ? "Todos los módulos" : h.title}`} closeOnBackdrop>
        <div className="mb-3 flex flex-col gap-2 sm:flex-row">
          <input className="input min-w-0 flex-1" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar en la ayuda…" aria-label="Buscar en la ayuda" />
          <button className="btn-outline-primary shrink-0 text-xs" onClick={() => { setAllHelp((v) => !v); setQuery(""); }}>{allHelp ? "Ayuda de esta pantalla" : "Ver todos los módulos"}</button>
        </div>
        <div className="max-h-[65dvh] space-y-4 overflow-y-auto pr-1">
          {entries.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No encontramos temas para esa búsqueda.</p>}
          {entries.map((section) => <section key={section.title}>
            {(allHelp || entries.length > 1) && <h3 className="mb-2 text-sm font-semibold text-slate-800">{section.title}</h3>}
            <ul className="space-y-2 text-sm text-slate-600">
              {section.items.map((it, i) => <li key={i} className="flex gap-2"><Icon name="check" size={15} className="mt-0.5 shrink-0 text-brand-500" /><span>{it}</span></li>)}
            </ul>
          </section>)}
        </div>
      </Modal>
    </>
  );
}
